import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

function getWeatherIconUrl(iconKey) {
  try {
    return require(`../../assets/weatherIocns/${iconKey}.png`);
  } catch {
    return null;
  }
}

function WeatherGlyph({ iconKey, className = 'h-10 w-10' }) {
  const src = getWeatherIconUrl(iconKey);
  if (src) {
    return (
      <img src={src} alt="" className={`object-contain ${className}`} />
    );
  }
  return <span className="text-3xl leading-none">⛅</span>;
}

const FORECAST_CARD_STRIP_ID = 'forecast-card-strip-scroll';

export default function ForecastCardStrip({ points, loading, containerRef }) {
  if (loading && !points.length) {
    return (
      <div className="flex gap-3 overflow-hidden">
        {Array.from({ length: 8 }).map((_, i) => (
          <div
            key={i}
            className="glass h-[200px] min-w-[120px] flex-1 animate-pulse rounded-2xl bg-white/5"
          />
        ))}
      </div>
    );
  }

  if (!points.length) {
    return (
      <p className="py-8 text-center text-sm text-gray-400">
        No forecast data for this interval.
      </p>
    );
  }

  return (
    <div
      id={FORECAST_CARD_STRIP_ID}
      ref={containerRef}
      data-forecast-card-strip="true"
      className="-mx-1 flex w-full gap-3 overflow-x-auto px-1 pb-2 thin-scrollbar scroll-smooth"
    >
      {points.map((p) => (
        <div
          key={p.id}
          className="glass flex min-w-[118px] max-w-[118px] shrink-0 flex-col items-center rounded-2xl px-3 py-4 sm:min-w-[124px] sm:max-w-[124px]"
        >
          <p className="text-center text-xs font-medium text-gray-300">
            {p.label}
          </p>
          <div className="my-3 flex h-12 items-center justify-center">
            <WeatherGlyph iconKey={p.iconKey} />
          </div>
          <p className="text-3xl font-light text-white">
            {p.tempF != null ? `${p.tempF}°` : '—'}
          </p>
          <div
            aria-label={`${p.popPercent}% chance of precipitation`}
            style={{
              width: 78,
              height: 56,
              marginTop: 16,
              borderRadius: 9999,
              backgroundColor: 'rgba(117, 126, 145, 0.78)',
              boxShadow:
                'inset 0 1px 8px rgba(255,255,255,0.12), inset 0 -6px 10px rgba(15,23,42,0.12)',
              overflow: 'hidden',
              position: 'relative',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                position: 'absolute',
                right: 0,
                bottom: 0,
                left: 0,
                height: `${Math.min(100, Math.max(0, Number(p.popPercent) || 0))}%`,
                borderRadius: 9999,
                background:
                  'linear-gradient(180deg, #67e8f9 0%, #38bdf8 58%, #0ea5e9 100%)',
                transition: 'height 700ms ease-out',
              }}
            />
          </div>
          <p className="mt-1.5 text-xs text-blue-300">{p.popPercent}%</p>
          <div className="mt-3 flex items-center gap-1 text-xs text-gray-300">
            <span className="text-sky-300" aria-hidden>
              💨
            </span>
            <span>{p.windMph}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

export function ForecastScrollButtons({ onScroll, scrollRef }) {
  const getScrollTarget = () => {
    const refTarget = scrollRef?.current;
    if (refTarget && refTarget.scrollWidth > refTarget.clientWidth) {
      return refTarget;
    }

    const idTarget = document.getElementById(FORECAST_CARD_STRIP_ID);
    if (idTarget && idTarget.scrollWidth > idTarget.clientWidth) {
      return idTarget;
    }

    return document.querySelector(
      '[data-forecast-card-strip="true"], .thin-scrollbar'
    );
  };

  const scroll = (dir) => {
    if (onScroll) {
      onScroll(dir);
      return;
    }

    const el = getScrollTarget();
    if (!el) return;

    const scrollAmount = Math.max(260, Math.floor(el.clientWidth * 0.75));
    const nextLeft = Math.max(
      0,
      Math.min(el.scrollWidth - el.clientWidth, el.scrollLeft + dir * scrollAmount)
    );

    if (typeof el.scrollBy === 'function') {
      el.scrollBy({ left: dir * scrollAmount, behavior: 'smooth' });
      return;
    }

    el.scrollLeft = nextLeft;
  };

  return (
    <div className="flex shrink-0 items-center gap-2">
      <button
        type="button"
        onClick={() => scroll(-1)}
        className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white transition hover:bg-white/10"
        aria-label="Scroll forecast left"
      >
        <ChevronLeftIcon className="h-5 w-5" />
      </button>
      <button
        type="button"
        onClick={() => scroll(1)}
        className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white transition hover:bg-white/10"
        aria-label="Scroll forecast right"
      >
        <ChevronRightIcon className="h-5 w-5" />
      </button>
    </div>
  );
}
